import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";

import { HeroAvatar, useHeroLookup } from "@/features/matches";
import { cn } from "@/shared/lib";

import type { HighlightsMatch } from "../../model/Highlight";
import { useMatchMeta } from "./useMatchMeta";

const VICTORY = msg`Victory`;
const DEFEAT = msg`Defeat`;
const KDA_SPOKEN = msg`Kills {kills}, deaths {deaths}, assists {assists}`;

type MatchHeadlineProps = {
  match: HighlightsMatch;
  // Есть — имя героя становится заголовком блока матча с этим id; нет — просто текст.
  headingId?: string;
  size?: "large" | "small";
  // Метка рядом с K/D/A («Из разбора»).
  extra?: ReactNode;
};

// Какой это матч: портрет и герой, победа или поражение, K/D/A, когда и сколько шёл.
// Шапка блока матча в разделе и плеера клипа (доски 18д и 18е).
export const MatchHeadline = ({ match, headingId, size = "large", extra }: MatchHeadlineProps) => {
  const { i18n } = useLingui();
  const hero = useHeroLookup()(match.hero_id);
  const meta = useMatchMeta()(match);
  const Name = headingId === undefined ? "span" : "h2";
  const { kills, deaths, assists } = match;
  return (
    <span className="flex min-w-0 items-center gap-3">
      <HeroAvatar name={hero.name} iconUrl={hero.iconUrl} size={size === "large" ? 48 : 36} />
      <span className="flex min-w-0 flex-col gap-0.5 leading-snug">
        <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <Name
            id={headingId}
            className={cn("m-0 font-semibold", size === "large" ? "text-lg" : "text-base")}
          >
            {hero.name}
          </Name>
          {match.won !== null && (
            <span
              className={cn(
                "rounded-full px-2 py-px text-xs font-semibold",
                match.won ? "bg-good/15 text-good" : "bg-bad/15 text-bad",
              )}
            >
              {i18n._(match.won ? VICTORY : DEFEAT)}
            </span>
          )}
          {kills !== null && deaths !== null && assists !== null && (
            <span className="font-mono text-sm text-foreground-soft">
              <span className="sr-only">
                {i18n._({ ...KDA_SPOKEN, values: { kills, deaths, assists } })}
              </span>
              <span aria-hidden="true">
                {kills} / {deaths} / {assists}
              </span>
            </span>
          )}
          {extra}
        </span>
        {meta !== "" && <span className="text-[13px] text-subtle">{meta}</span>}
      </span>
    </span>
  );
};
